import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Container,
    TextField,
    Typography,
    Paper,
    Alert,
    InputAdornment,
    IconButton,
    CircularProgress,
    Stack
} from '@mui/material';
import { Visibility, VisibilityOff, Email, CheckCircle, RadioButtonUnchecked } from '@mui/icons-material';
import AuthService from '../../ui/services/auth-service';
import logo from '../../assets/webp/logo_complete.webp';
import logoWhite from '../../assets/webp/logo_complete_white.webp';
import { useThemeMode } from '../../ui/context/theme/ThemeProvider';

export const RegisterPage = () => {
    const navigate = useNavigate();
    const { mode } = useThemeMode();
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [warningMessage, setWarningMessage] = useState(null);

    // Estado para los criterios de validación de contraseña
    const [passwordCriteria, setPasswordCriteria] = useState({
        minLength: false,
        hasUppercase: false,
        hasLowercase: false,
        hasNumber: false,
        hasSymbol: false
    });

    // Validar contraseña en tiempo real
    const validatePassword = (password) => {
        setPasswordCriteria({
            minLength: password.length >= 10,
            hasUppercase: /[A-Z]/.test(password),
            hasLowercase: /[a-z]/.test(password),
            hasNumber: /\d/.test(password),
            hasSymbol: /[!@#$%^&*(),.?":{}|<>_\-+=[\]\\;'`~/]/.test(password)
        });
    };

    // Verificar si todos los criterios se cumplen
    const isPasswordValid = Object.values(passwordCriteria).every(criterion => criterion);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        
        // Validar contraseña en tiempo real
        if (name === 'password') {
            validatePassword(value);
        }
        
        // Limpiar error del campo al escribir
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        // Validar firstName
        if (!formData.firstName.trim()) {
            newErrors.firstName = 'El nombre es requerido';
        } else if (formData.firstName.trim().length < 2) {
            newErrors.firstName = 'El nombre debe tener al menos 2 caracteres';
        }

        // Validar lastName
        if (!formData.lastName.trim()) {
            newErrors.lastName = 'El apellido es requerido';
        } else if (formData.lastName.trim().length < 2) {
            newErrors.lastName = 'El apellido debe tener al menos 2 caracteres';
        }

        // Validar email
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formData.email.trim()) {
            newErrors.email = 'El email es requerido';
        } else if (!emailRegex.test(formData.email)) {
            newErrors.email = 'Email inválido';
        }

        // Validar password
        if (!formData.password) {
            newErrors.password = 'La contraseña es requerida';
        } else if (!isPasswordValid) {
            newErrors.password = 'La contraseña no cumple con todos los criterios de seguridad';
        }

        // Validar confirmPassword
        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Confirma tu contraseña';
        } else if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Las contraseñas no coinciden';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setErrors({});
        setWarningMessage(null);

        try {
            // eslint-disable-next-line no-unused-vars
            const { confirmPassword, ...registrationData } = formData;
            const result = await AuthService.signup(registrationData);

            if (result.success) {
                setSuccess(true);
                
                // Capturar warning si existe (cuenta vinculada a Nextcloud existente)
                if (result.warning) {
                    setWarningMessage(result.warning);
                }
                
                // Redirigir al login después de 3 segundos
                setTimeout(() => {
                    navigate('/login');
                }, 3000);
            } else {
                setErrors({ submit: result.error });
            }
        // eslint-disable-next-line no-unused-vars
        } catch (error) {
            setErrors({ submit: 'Error inesperado. Por favor intenta de nuevo.' });
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <Box
                sx={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2
                }}
            >
                <Container component="main" maxWidth="sm">
                    <Paper
                        elevation={6}
                        sx={{
                            p: { xs: 5, sm: 6 },
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            maxWidth: '500px',
                            mx: 'auto',
                            borderRadius: 2
                        }}
                    >
                        <Email sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
                        <Typography component="h1" variant="h5" gutterBottom>
                            ¡Registro Exitoso!
                        </Typography>
                        
                        {warningMessage && (
                            <Alert severity="info" sx={{ width: '100%', mt: 2, mb: 2 }}>
                                {warningMessage}
                            </Alert>
                        )}
                        
                        <Typography variant="body1" color="text.secondary" align="center" sx={{ mt: 1 }}>
                            Hemos enviado un correo de confirmación a <strong>{formData.email}</strong>
                        </Typography>
                        <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
                            Por favor revisa tu bandeja de entrada y haz clic en el enlace para activar tu cuenta.
                        </Typography>
                        <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 2 }}>
                            Redirigiendo a login en 3 segundos...
                        </Typography>
                    </Paper>
                </Container>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2
            }}
        >
            <Container component="main" maxWidth="sm">
                <Paper
                    elevation={6}
                    sx={{
                        p: { xs: 5, sm: 6 },
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        maxWidth: '520px',
                        mx: 'auto',
                        borderRadius: 2
                    }}
                >
                    <Box
                        component="img"
                        src={mode === 'dark' ? logoWhite : logo}
                        alt="Neftis SecureSign"
                        sx={{ 
                            width: '60%',
                            maxWidth: '240px',
                            mb: 3,
                            mt: 0
                        }}
                    />
                    <Typography component="h1" variant="h5" fontWeight="600" gutterBottom>
                        Crear Cuenta
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Regístrate para comenzar a usar SecureSign
                    </Typography>

                    {errors.submit && (
                        <Alert severity="error" sx={{ width: '100%', mt: 1, mb: 2 }}>
                            {errors.submit}
                        </Alert>
                    )}

                    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                id="firstName"
                                label="Nombre"
                                name="firstName"
                                autoComplete="given-name"
                                autoFocus
                                value={formData.firstName}
                                onChange={handleChange}
                                error={!!errors.firstName}
                                helperText={errors.firstName}
                                disabled={loading}
                            />
                            <TextField
                                margin="normal"
                                required
                                fullWidth
                                id="lastName"
                                label="Apellido"
                                name="lastName"
                                autoComplete="family-name"
                                value={formData.lastName}
                                onChange={handleChange}
                                error={!!errors.lastName}
                                helperText={errors.lastName}
                                disabled={loading}
                            />
                        </Box>

                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            id="email"
                            label="Correo Electrónico"
                            name="email"
                            autoComplete="email"
                            value={formData.email}
                            onChange={handleChange}
                            error={!!errors.email}
                            helperText={errors.email}
                            disabled={loading}
                        />

                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="password"
                            label="Contraseña"
                            type={showPassword ? 'text' : 'password'}
                            id="password"
                            autoComplete="new-password"
                            value={formData.password}
                            onChange={handleChange}
                            error={!!errors.password}
                            helperText={errors.password}
                            disabled={loading}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />

                        {/* Criterios de seguridad de contraseña */}
                        {formData.password && (
                            <Box sx={{ mt: 1, mb: 2 }}>
                                <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
                                    Criterios de seguridad:
                                </Typography>
                                <Stack spacing={0.5}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {passwordCriteria.minLength ? (
                                            <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                                        ) : (
                                            <RadioButtonUnchecked sx={{ fontSize: 16, color: 'text.disabled' }} />
                                        )}
                                        <Typography 
                                            variant="caption" 
                                            sx={{ 
                                                color: passwordCriteria.minLength ? 'success.main' : 'text.secondary',
                                                fontWeight: passwordCriteria.minLength ? 500 : 400
                                            }}
                                        >
                                            Mínimo 10 caracteres
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {passwordCriteria.hasUppercase ? (
                                            <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                                        ) : (
                                            <RadioButtonUnchecked sx={{ fontSize: 16, color: 'text.disabled' }} />
                                        )}
                                        <Typography 
                                            variant="caption" 
                                            sx={{ 
                                                color: passwordCriteria.hasUppercase ? 'success.main' : 'text.secondary',
                                                fontWeight: passwordCriteria.hasUppercase ? 500 : 400
                                            }}
                                        >
                                            Al menos una letra mayúscula (A-Z)
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {passwordCriteria.hasLowercase ? (
                                            <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                                        ) : (
                                            <RadioButtonUnchecked sx={{ fontSize: 16, color: 'text.disabled' }} />
                                        )}
                                        <Typography 
                                            variant="caption" 
                                            sx={{ 
                                                color: passwordCriteria.hasLowercase ? 'success.main' : 'text.secondary',
                                                fontWeight: passwordCriteria.hasLowercase ? 500 : 400
                                            }}
                                        >
                                            Al menos una letra minúscula (a-z)
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {passwordCriteria.hasNumber ? (
                                            <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                                        ) : (
                                            <RadioButtonUnchecked sx={{ fontSize: 16, color: 'text.disabled' }} />
                                        )}
                                        <Typography 
                                            variant="caption" 
                                            sx={{ 
                                                color: passwordCriteria.hasNumber ? 'success.main' : 'text.secondary',
                                                fontWeight: passwordCriteria.hasNumber ? 500 : 400
                                            }}
                                        >
                                            Al menos un número (0-9)
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {passwordCriteria.hasSymbol ? (
                                            <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                                        ) : (
                                            <RadioButtonUnchecked sx={{ fontSize: 16, color: 'text.disabled' }} />
                                        )}
                                        <Typography 
                                            variant="caption" 
                                            sx={{ 
                                                color: passwordCriteria.hasSymbol ? 'success.main' : 'text.secondary',
                                                fontWeight: passwordCriteria.hasSymbol ? 500 : 400
                                            }}
                                        >
                                            Al menos un símbolo (!@#$%^&*...)
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Box>
                        )}

                        <TextField
                            margin="normal"
                            required
                            fullWidth
                            name="confirmPassword"
                            label="Confirmar Contraseña"
                            type={showConfirmPassword ? 'text' : 'password'}
                            id="confirmPassword"
                            autoComplete="new-password"
                            value={formData.confirmPassword}
                            onChange={handleChange}
                            error={!!errors.confirmPassword}
                            helperText={errors.confirmPassword}
                            disabled={loading}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            edge="end"
                                        >
                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            size="large"
                            sx={{ mt: 2, mb: 2, py: 1.2 }}
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                                    Registrando...
                                </>
                            ) : (
                                'Registrarse'
                            )}
                        </Button>

                        <Box sx={{ textAlign: 'center', mt: 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                ¿Ya tienes una cuenta?{' '}
                                <Link
                                    to="/login"
                                    style={{
                                        color: '#1976d2',
                                        textDecoration: 'none',
                                        fontWeight: '600'
                                    }}
                                >
                                    Inicia Sesión
                                </Link>
                            </Typography>
                        </Box>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default RegisterPage;
